import { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import { fi } from '../locales/fi';
import { en } from '../locales/en';
import { sv } from '../locales/sv';
import { useInstance } from '../contexts/InstanceContext';
import { normalizeInstanceLanguage } from '../../../../packages/shared/instanceLanguage.js';
import API_ENDPOINTS from '../config/api';

const TranslationContext = createContext();

const translations = {
  fi,
  en,
  sv,
};

/** { 'report.header.title': text } → { report: { header: { title: text } } } */
function unflatten(flat) {
  const out = {};
  for (const [path, text] of Object.entries(flat || {})) {
    const keys = path.split('.');
    let node = out;
    for (const k of keys.slice(0, -1)) node = (node[k] ??= {});
    node[keys[keys.length - 1]] = text;
  }
  return out;
}

function applyVars(str, vars) {
  if (typeof str !== 'string' || !vars) return str;
  return str.replace(/\{\{(\w+)\}\}/g, (_, name) => (vars[name] != null ? String(vars[name]) : ''));
}

export const TranslationProvider = ({ children, defaultLanguage = 'fi' }) => {
  const { currentInstance } = useInstance();
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('kuutio-language');
    return normalizeInstanceLanguage(saved || defaultLanguage);
  });

  // Workshop instance language overrides local preference when instance is loaded
  useEffect(() => {
    if (!currentInstance?.id) return;
    const raw = currentInstance.language ?? currentInstance.settings?.language;
    if (raw != null && String(raw).trim() !== '') {
      setLanguage(normalizeInstanceLanguage(raw));
    }
  }, [currentInstance?.id, currentInstance?.language, currentInstance?.settings?.language]);

  // A report in a language without a hand-written locale (#1442) loads its
  // interface text here; it is never saved as the app's language.
  // Until it arrives, t() answers in English.
  const [remoteLocales, setRemoteLocales] = useState({});
  const loadingRef = useRef(new Map()); // lang → promise, one fetch per language
  const loadLocale = useCallback((lang) => {
    if (translations[lang]) return Promise.resolve();
    if (!loadingRef.current.has(lang)) {
      const loading = fetch(API_ENDPOINTS.REPORT_UI_STRINGS(lang)).then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const { strings } = await res.json();
        setRemoteLocales((prev) => ({ ...prev, [lang]: unflatten(strings) }));
      });
      loading.catch(() => loadingRef.current.delete(lang));
      loadingRef.current.set(lang, loading);
    }
    return loadingRef.current.get(lang);
  }, []);

  useEffect(() => {
    if (translations[language]) localStorage.setItem('kuutio-language', language);
  }, [language]);

  // A workshop may say a thing its own way: settings.textOverrides maps a translation
  // key to the instance's text (TKI 29.10.2026: "Ratkaisut" → "Edellytykset" on the
  // solutions screens). Data, not code: the instance's language stays one language.
  const textOverrides = currentInstance?.settings?.textOverrides;

  const t = useCallback((key, vars) => {
    const override = textOverrides && typeof textOverrides[key] === 'string' ? textOverrides[key] : null;
    if (override !== null) return applyVars(override, vars);
    const keys = key.split('.');
    // A loaded remote language falls back to English, string by string.
    const remote = !translations[language];
    const fallback = remote ? translations.en : translations.fi;
    let result = remote ? (remoteLocales[language] || {}) : translations[language];

    for (const k of keys) {
      if (result && typeof result === 'object') {
        result = result[k];
      } else {
        result = keys.reduce((acc, kk) => acc?.[kk], fallback);
        if (result === undefined || result === null) {
          console.warn(`Translation key not found: ${key}`);
          return key;
        }
        break;
      }
    }

    if (remote && result === undefined) result = keys.reduce((acc, kk) => acc?.[kk], fallback);
    if (typeof result === 'string') return applyVars(result, vars);
    return result;
  }, [language, textOverrides, remoteLocales]);

  const value = {
    language,
    setLanguage,
    loadLocale,
    t,
    languages: Object.keys(translations),
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};
